import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff, Plus, X, AlertCircle } from 'lucide-react';
import { ValidationRule, validateField } from '../../utils/propertyValidation';

// PropertyField fronts many input types (string, number, boolean, object,
// array, undefined). The public value contract is intentionally permissive
// for caller compatibility; internally we narrow via `toText` and
// type-specific renderers before touching DOM inputs.
 
export type PropertyFieldValue = any;

export interface PropertyFieldConfig {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'multiselect' | 'date' | 'datetime' | 'password' | 'email' | 'url' | 'expression' | 'object' | 'array' | 'combobox';
  placeholder?: string;
  description?: string;
  defaultValue?: PropertyFieldValue;
  options?: { value: string; label: string }[];
  validation?: ValidationRule;
  disabled?: boolean;
  rows?: number;
  condition?: {
      field: string;
      operator: 'equals' | 'notEquals' | 'contains';
      value: PropertyFieldValue;
  };
  itemType?: 'text' | 'number'; // For array type
}

interface PropertyFieldProps {
  config: PropertyFieldConfig;
  value: PropertyFieldValue;
  onChange: (key: string, value: PropertyFieldValue) => void;
  error?: string;
}

// Narrow an unknown value to a string for text-like inputs.
const toText = (v: PropertyFieldValue): string => {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

// Shared input class string. Module-level so helper renderers can reuse it.
const INPUT_CLASSES =
  "w-full px-3 py-2 text-sm border rounded-lg border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 placeholder-slate-400 transition-all font-sans";

// ---- Per-type render helpers (extracted from the monster switch) -----------

interface TextLikeArgs {
  config: PropertyFieldConfig;
  value: string;
  onChange: (val: PropertyFieldValue) => void;
}

const renderText = ({ config, value, onChange }: TextLikeArgs) => (
  <input
    type={config.type === 'email' ? 'email' : config.type === 'url' ? 'url' : 'text'}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={config.placeholder}
    disabled={config.disabled}
    className={INPUT_CLASSES}
  />
);

const renderTextarea = ({ config, value, onChange }: TextLikeArgs) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    rows={config.rows || 3}
    placeholder={config.placeholder}
    disabled={config.disabled}
    className={INPUT_CLASSES}
  />
);

const renderExpression = ({ config, value, onChange }: TextLikeArgs) => (
  <div className="relative">
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={config.rows || 2}
      placeholder={config.placeholder || "e.g. variables.amount > 100"}
      disabled={config.disabled}
      className="w-full px-3 py-2 text-sm border rounded-lg border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 focus:outline-none focus:ring-2 focus:ring-amber-500/50 font-mono text-slate-700 dark:text-slate-300 placeholder-amber-400/50"
    />
    <div className="absolute top-1 right-2 text-[10px] text-amber-600 font-semibold pointer-events-none">JS</div>
  </div>
);

interface ObjectArgs {
  config: PropertyFieldConfig;
  jsonText: string;
  internalError: string | null;
  onJsonChange: (text: string) => void;
}

const renderObject = ({ config, jsonText, internalError, onJsonChange }: ObjectArgs) => (
  <div>
    <textarea
      value={jsonText}
      onChange={(e) => onJsonChange(e.target.value)}
      rows={config.rows || 5}
      placeholder="{}"
      className={`w-full px-3 py-2 text-xs border rounded-lg bg-slate-50 dark:bg-slate-900 font-mono focus:outline-none focus:ring-1 ${internalError ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-700 focus:ring-teal-500 focus:border-teal-500'}`}
    />
  </div>
);

interface ArrayArgs {
  config: PropertyFieldConfig;
  items: ReadonlyArray<unknown>;
  onItemChange: (index: number, val: string | number) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
}

const renderArray = ({ config, items, onItemChange, onRemove, onAdd }: ArrayArgs) => (
  <div className="space-y-2">
    {items.map((item, index) => (
      <div key={index} className="flex gap-2">
        <input
          type={config.itemType === 'number' ? 'number' : 'text'}
          value={toText(item)}
          onChange={(e) =>
            onItemChange(index, config.itemType === 'number' ? Number(e.target.value) : e.target.value)
          }
          className={INPUT_CLASSES}
        />
        <button
          onClick={() => onRemove(index)}
          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    ))}
    <button
      onClick={onAdd}
      className="flex items-center gap-1.5 text-xs text-teal-600 dark:text-teal-400 font-medium hover:text-teal-700 px-1 py-1"
    >
      <Plus size={12} /> Add Item
    </button>
  </div>
);

interface BooleanArgs {
  value: boolean;
  onChange: (val: boolean) => void;
}

const renderBoolean = ({ value, onChange }: BooleanArgs) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
        value ? 'bg-teal-600' : 'bg-slate-200 dark:bg-slate-700'
      }`}
    >
      <span
        className={`${
          value ? 'translate-x-5' : 'translate-x-1'
        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform shadow-sm`}
      />
    </button>
    <span className="text-sm text-slate-600 dark:text-slate-400 select-none">{value ? 'Yes' : 'No'}</span>
  </div>
);

const renderSelect = ({ config, value, onChange }: TextLikeArgs) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={config.disabled}
    className={INPUT_CLASSES}
  >
    {config.options?.map(opt => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

interface PasswordArgs {
  config: PropertyFieldConfig;
  value: string;
  showPassword: boolean;
  onChange: (val: PropertyFieldValue) => void;
  onToggleShow: () => void;
}

const renderPassword = ({ config, value, showPassword, onChange, onToggleShow }: PasswordArgs) => (
  <div className="relative">
    <input
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={config.placeholder}
      disabled={config.disabled}
      className={`${INPUT_CLASSES} pr-8`}
    />
    <button
      type="button"
      onClick={onToggleShow}
      className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600"
    >
      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
    </button>
  </div>
);

interface NumberArgs {
  config: PropertyFieldConfig;
  value: string;
  onNumberChange: (n: number) => void;
}

const renderNumber = ({ config, value, onNumberChange }: NumberArgs) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onNumberChange(Number(e.target.value))}
    placeholder={config.placeholder}
    disabled={config.disabled}
    className={INPUT_CLASSES}
  />
);

interface ComboboxArgs {
  config: PropertyFieldConfig;
  currentValue: string;
  comboboxOpen: boolean;
  comboboxFilter: string;
  comboboxRef: React.RefObject<HTMLDivElement | null>;
  setComboboxOpen: (open: boolean) => void;
  setComboboxFilter: (filter: string) => void;
  onChange: (val: PropertyFieldValue) => void;
}

const renderCombobox = ({
  config,
  currentValue,
  comboboxOpen,
  comboboxFilter,
  comboboxRef,
  setComboboxOpen,
  setComboboxFilter,
  onChange,
}: ComboboxArgs) => {
  const options = config.options || [];
  const filterLower = comboboxFilter.toLowerCase();
  // When input is empty OR matches the committed value exactly, show ALL options.
  // Otherwise filter by typed text.
  const showAll = comboboxFilter === '' || comboboxFilter === (currentValue || '');
  const filtered = showAll
    ? options
    : options.filter(opt => opt.label.toLowerCase().includes(filterLower));
  const exactMatch = options.some(opt => opt.value.toLowerCase() === filterLower);
  const showAddEntry = comboboxFilter.trim() !== '' && !exactMatch;

  return (
    <div className="relative" ref={comboboxRef}>
      <input
        type="text"
        value={comboboxOpen ? comboboxFilter : (currentValue || '')}
        onChange={(e) => {
          setComboboxFilter(e.target.value);
          if (!comboboxOpen) setComboboxOpen(true);
        }}
        onFocus={() => {
          // Show all entries on focus — start with empty filter.
          setComboboxFilter('');
          setComboboxOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = comboboxFilter.trim();
            if (trimmed) {
              onChange(trimmed);
              setComboboxOpen(false);
            }
          } else if (e.key === 'Escape') {
            setComboboxOpen(false);
            setComboboxFilter('');
          }
        }}
        placeholder={config.placeholder}
        disabled={config.disabled}
        className={`${INPUT_CLASSES} pr-14`}
      />
      <div className="absolute right-2 top-2.5 flex items-center gap-1 text-slate-400">
        {currentValue && (
          <button
            type="button"
            onClick={() => { onChange(undefined); setComboboxFilter(''); setComboboxOpen(false); }}
            className="hover:text-slate-600"
            title="Clear"
          >
            <X size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (comboboxOpen) {
              setComboboxOpen(false);
            } else {
              setComboboxFilter('');
              setComboboxOpen(true);
            }
          }}
          className="hover:text-slate-600"
          tabIndex={-1}
          title="Toggle options"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M1 3l4 4 4-4z"/></svg>
        </button>
      </div>
      {comboboxOpen && (filtered.length > 0 || showAddEntry) && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setComboboxFilter(opt.value); setComboboxOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
            >
              {opt.label}
            </button>
          ))}
          {showAddEntry && (
            <button
              type="button"
              onClick={() => { onChange(comboboxFilter); setComboboxOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm text-teal-600 dark:text-teal-400 font-medium hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors border-t border-slate-200 dark:border-slate-700 flex items-center gap-1.5"
            >
              <Plus size={12} /> {comboboxFilter}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ---- Main component --------------------------------------------------------

export const PropertyField: React.FC<PropertyFieldProps> = ({ config, value, onChange, error: externalError }) => {
  const [internalError, setInternalError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [comboboxFilter, setComboboxFilter] = useState('');
  const comboboxRef = useRef<HTMLDivElement>(null);

  const currentValue: PropertyFieldValue = value !== undefined ? value : (config.defaultValue ?? '');

  // Close combobox on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (comboboxRef.current && !comboboxRef.current.contains(e.target as Node)) {
        setComboboxOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync JSON text for object type when external value changes
  useEffect(() => {
    if (config.type === 'object' && value) {
        try {
            setJsonText(JSON.stringify(value, null, 2));
        } catch (e) {
            setJsonText('{}');
        }
    }
  }, [value, config.type]);

  const handleChange = (val: PropertyFieldValue) => {
    // Validate immediately
    if (config.validation) {
        const err = validateField(val, config.validation);
        setInternalError(err);
    } else {
        setInternalError(null);
    }
    onChange(config.name, val);
  };

  const handleJsonChange = (text: string) => {
      setJsonText(text);
      try {
          const parsed = JSON.parse(text);
          setInternalError(null);
          onChange(config.name, parsed);
      } catch (e) {
          setInternalError("Invalid JSON format");
      }
  };

  const handleArrayAdd = () => {
      const newItem: string | number = config.itemType === 'number' ? 0 : '';
      const newArray = Array.isArray(currentValue) ? [...currentValue, newItem] : [newItem];
      handleChange(newArray);
  };

  const handleArrayRemove = (index: number) => {
      if (!Array.isArray(currentValue)) return;
      const newArray = currentValue.filter((_, i) => i !== index);
      handleChange(newArray);
  };

  const handleArrayItemChange = (index: number, val: string | number) => {
      if (!Array.isArray(currentValue)) return;
      const newArray = [...currentValue];
      newArray[index] = val;
      handleChange(newArray);
  };

  const textValue = toText(currentValue);
  const renderers: Record<string, () => React.ReactNode> = {
    textarea: () =>
      renderTextarea({ config, value: textValue, onChange: handleChange }),
    expression: () =>
      renderExpression({ config, value: textValue, onChange: handleChange }),
    object: () =>
      renderObject({ config, jsonText, internalError, onJsonChange: handleJsonChange }),
    array: () =>
      renderArray({
        config,
        items: Array.isArray(currentValue) ? currentValue : [],
        onItemChange: handleArrayItemChange,
        onRemove: handleArrayRemove,
        onAdd: handleArrayAdd,
      }),
    boolean: () => renderBoolean({ value: !!currentValue, onChange: handleChange }),
    select: () => renderSelect({ config, value: textValue, onChange: handleChange }),
    combobox: () =>
      renderCombobox({
        config,
        currentValue: textValue,
        comboboxOpen,
        comboboxFilter,
        comboboxRef,
        setComboboxOpen,
        setComboboxFilter,
        onChange: handleChange,
      }),
    password: () =>
      renderPassword({
        config,
        value: textValue,
        showPassword,
        onChange: handleChange,
        onToggleShow: () => setShowPassword(!showPassword),
      }),
    number: () =>
      renderNumber({ config, value: textValue, onNumberChange: handleChange }),
  };

  const renderInput = () => {
    const handler = renderers[config.type];
    if (handler) return handler();
    // Fallback to text for: text, email, url, date, datetime, multiselect
    return renderText({ config, value: textValue, onChange: handleChange });
  };

  return (
    <div className="space-y-1.5 mb-5">
      <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center justify-between">
        <span>
            {config.label}
            {config.validation?.required && <span className="text-teal-600 ml-1">*</span>}
        </span>
        {config.type === 'expression' && (
             <span title="Supports JavaScript expressions" className="text-slate-400 cursor-help"><AlertCircle size={10}/></span>
        )}
      </label>
      {renderInput()}
      {config.description && <p className="text-[11px] text-slate-500 mt-1 leading-snug">{config.description}</p>}
      {(externalError || internalError) && (
          <p className="text-[11px] text-red-600 font-medium mt-1">{externalError || internalError}</p>
      )}
    </div>
  );
};